import { Alert, AlertTitle, Box, Button, Checkbox, FormControlLabel, TextField } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useConfirm } from 'material-ui-confirm';
import style from './CompanyFormWidget.module.css';
import { patchCompany, postCompany } from '../../shared/api/api';
import TimeZoneSelector, { timeZoneOptions } from '../../shared/ui/TimeZoneSelector';
import { getBrowserTimeshift } from '../../shared/utils/timezones';
import useAuth from '../../shared/lib/hooks/useAuth';
import { NODES } from '../../entities/tree/config';

const initialCompany = {
  name: '',
  site: '',
  address: '',
  timeZone: getBrowserTimeshift(),
};

// Отображает форму редактирования с инфо о компании
function CompanyFormWidget(props) {
  const {
    selectedCompany,
    readOnly,
    updateTreeForCompany,
    updateTree,
    setIsFormChanged,
    isFormChanged,
    isErr,
    setIsErr,
  } = props;
  const { isDealer, isAdmin, user } = useAuth();

  const [company, setCompany] = useState({ ...initialCompany, ...selectedCompany });
  const [errors, setErrors] = useState({ ...initialCompany });
  const [isDealerField, setIsDealerField] = useState(company?.isDealer);

  const [isCorrect, setIsCorrect] = useState(false);

  const changeTextField = (event) => {
    if (setIsErr) setIsErr(false);
    setIsCorrect(false);
    setCompany((prev) => ({ ...prev, [event.target.id]: event.target.value }));
    if (errors[event.target.id]) {
      setErrors((prev) => ({ ...prev, [event.target.id]: event.target.value.trim() === '' }));
    }
    if (!isFormChanged) {
      setIsFormChanged(true);
    }
  };

  const handleChangeIsDealer = () => {
    setIsDealerField((prev) => !prev);

    if (!isFormChanged) {
      setIsFormChanged(true);
    }
  };

  const resetForm = useCallback(
    (companyInfo) => {
      setIsFormChanged(false);
      setErrors({ ...initialCompany });
      const currentCompany = { ...initialCompany, ...companyInfo };
      setCompany(currentCompany);
      setIsDealerField(!!currentCompany?.isDealer);
    },
    [setIsFormChanged],
  );

  const confirm = useConfirm();
  const handleResetForm = (companyInfo) => {
    confirm({
      title: 'Отменить изменения?',
      confirmationText: 'Да',
      cancellationText: 'Нет',
      description: `Вы уверены, что хотите отменить изменения в форме?`,
    }).then(() => {
      resetForm(companyInfo);
    });
  };

  const changeTimeZone = (event) => {
    const { value } = event.target;

    const timeZoneId = timeZoneOptions.find((e) => e?.value === value)?.region;

    setIsErr?.(false);
    setIsCorrect(false);
    setCompany((prev) => ({
      ...prev,
      customAugmentationEx: {
        ...(prev.customAugmentationEx || {}),
        general: { timeZone: value, timeZoneId: timeZoneId?.split(',')[0] },
      },
    }));
    if (!isFormChanged) {
      setIsFormChanged(true);
    }
  };

  const valueTimeZone = company.customAugmentationEx?.general?.timeZone;

  const saveForm = useCallback(() => {
    let isError = false;
    Object.keys(initialCompany).forEach((key) => {
      if (!isError) {
        isError = !company[key];
      }
      setErrors((prev) => ({ ...prev, [key]: !company[key] }));
    });
    if (!isError) {
      const body = {
        name: company.name,
        site: company.site,
        address: company.address,
        customAugmentationEx: company.customAugmentationEx,
      };

      if (isDealer) {
        body.dealerCompany = { companyId: user?.company?.companyId };
      } else {
        body.isDealer = isDealerField;
      }

      if (selectedCompany.companyId) {
        patchCompany({ ...body, companyId: selectedCompany.companyId })
          .then((res) => {
            if (isDealer) {
              updateTree(selectedCompany.key, {
                ...company,
                nodeType: NODES.CLIENT.TYPE,
              });
            } else {
              updateTreeForCompany(selectedCompany.key, company);
            }

            resetForm(res);
            if (res.error) {
              setIsErr(true);
            } else {
              setIsCorrect(true);
            }
          })
          .catch((error) => {
            throw error;
          });
      } else {
        postCompany(body)
          .then((res) => {
            if (isDealer) {
              updateTree(
                selectedCompany.key,
                {
                  ...res,
                  ...company,
                  nodeType: NODES.CLIENT.TYPE,
                },
                true,
              );
            } else {
              updateTreeForCompany(selectedCompany.key, { ...res, ...company }, true);
            }
            resetForm(res);
            if (res.error) {
              setIsErr(true);
            } else {
              setIsCorrect(true);
            }
          })
          .catch((error) => {
            throw error;
          });
      }
    }
  }, [company, resetForm, updateTreeForCompany, selectedCompany, valueTimeZone, user, isDealerField]);

  useEffect(() => {
    resetForm(selectedCompany);
  }, [selectedCompany, resetForm]);

  return (
    <Box className={style.companyFormWidget}>
      <TextField
        fullWidth
        id="name"
        label="Наименование"
        value={company.name}
        onChange={changeTextField}
        required
        InputProps={{
          readOnly,
        }}
        error={Boolean(errors.name)}
        helperText={errors.name ? 'Поле не может быть пустым' : null}
        onBlur={(event) => {
          setErrors((prev) => ({ ...prev, [event.target.id]: event.target.value.trim() === '' }));
        }}
      />
      <TextField
        fullWidth
        id="site"
        label="Сайт"
        value={company.site}
        onChange={changeTextField}
        required
        InputProps={{
          readOnly,
        }}
        error={Boolean(errors.site)}
        helperText={errors.site ? 'Поле не может быть пустым' : null}
        onBlur={(event) => {
          setErrors((prev) => ({ ...prev, [event.target.id]: event.target.value.trim() === '' }));
        }}
      />
      <TextField
        fullWidth
        id="address"
        label="Адрес"
        value={company.address}
        onChange={changeTextField}
        required
        InputProps={{
          readOnly,
        }}
        error={Boolean(errors.address)}
        helperText={errors.address ? 'Поле не может быть пустым' : null}
        onBlur={(event) => {
          setErrors((prev) => ({ ...prev, [event.target.id]: event.target.value.trim() === '' }));
        }}
      />
      <TimeZoneSelector value={valueTimeZone} onChange={changeTimeZone} />
      {!isErr && isCorrect && (
        <Alert sx={{ width: '100%' }} severity="success">
          <AlertTitle>Успешно</AlertTitle>
          Данные успешно сохранены
        </Alert>
      )}
      {isErr && (
        <Alert sx={{ width: '100%' }} severity="error">
          <AlertTitle>Ошибка</AlertTitle>
          Произошла ошибка
        </Alert>
      )}
      {isAdmin && !readOnly && (
        <FormControlLabel
          control={<Checkbox checked={isDealerField} onChange={handleChangeIsDealer} />}
          label="Дилер"
        />
      )}
      {readOnly ? null : (
        <div className={style.buttonts}>
          <Button
            disableElevation
            disabled={!isFormChanged}
            onClick={() => handleResetForm(selectedCompany)}
            variant="text"
          >
            Отмена
          </Button>
          <Button disableElevation disabled={!isFormChanged} onClick={() => saveForm()} variant="contained">
            Сохранить
          </Button>
        </div>
      )}
    </Box>
  );
}

CompanyFormWidget.propTypes = {
  selectedCompany: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.object])),
  readOnly: PropTypes.bool,
  updateTreeForCompany: PropTypes.func.isRequired,
  updateTree: PropTypes.func.isRequired,
  isFormChanged: PropTypes.bool.isRequired,
  setIsFormChanged: PropTypes.func.isRequired,
  isErr: PropTypes.bool,
  setIsErr: PropTypes.func,
};

CompanyFormWidget.defaultProps = {
  selectedCompany: null,
  readOnly: true,
  isErr: null,
  setIsErr: null,
};

export default CompanyFormWidget;
